/**
 * Frontend client for the incept-curriculum API.
 * Used by BenchmarkSetDetail to fetch leaves for on-the-fly request display.
 *
 * The curriculum API key is server-side only — requests are forwarded through
 * the `proxy-curriculum` Supabase edge function, which injects the key from
 * Supabase env. CRA bakes REACT_APP_* into the public bundle, so we
 * deliberately don't read the key client-side.
 */

import { supabase } from "./supabase";

// DB now stores curriculum/subject values that match the curriculum repo
// verbatim. SUBJECT_NAME_MAP is consulted only for the few AP subjects whose
// short codes (apush/apwh/apeh) differ from the curriculum repo's subject
// folders ("American History" / "World History").
// Lowercase DB/slug subject → canonical name expected by the curriculum API.
// Covers AP short codes (apush/apwh/...) plus the lowercased non-AP subjects
// stored in bm_benchmark_sets / bm_requests ("math", "ela", ...).
const SUBJECT_NAME_MAP = {
  apush: "American History",
  apwh: "World History",
  apeh: "World History",
  apeuro: "World History",
  chemistry: "Chemistry",
  math: "Math",
  ela: "Language",
  language: "Language",
  reading: "Reading",
  science: "Science",
  "social studies": "Social Studies",
  "reading and writing": "Reading and Writing",
};

// Lowercase DB/slug curriculum → canonical name expected by the curriculum
// API. Covers BenchmarkSets URL slugs (toSlug → "ap"/"cc"/...) and the
// legacy bm_benchmark_sets values ("common core").
const CURRICULUM_NAME_MAP = {
  ap: "AP",
  cc: "CC",
  "common core": "CC",
  sat: "SAT",
  teks: "TEKS",
  "athena-cc": "Athena-CC",
  "simon-cc": "Simon-CC",
};

/**
 * For AP subjects, the "grade" is a unit number that maps to a domain,
 * not a course. This maps AP subject → course name in the curriculum.
 */
const AP_COURSE_MAP = {
  apush: "AP United States History",
  apwh: "AP World History: Modern",
  apeh: "AP European History",
  apeuro: "AP European History",
  chemistry: "AP Chemistry",
};

/**
 * Domain prefix patterns for matching AP unit numbers.
 * e.g. grade "1" for apush → domain containing "Unit 1"
 * For chemistry the domain_id is just the unit number, so prefix is empty.
 */
const AP_DOMAIN_PREFIX = {
  apush: "AP USH Unit",
  apwh: "AP World Unit",
  apeh: "AP EUH Unit",
  apeuro: "AP EUH Unit",
  chemistry: "",
};

const resolveApiNames = (curriculum, subject) => {
  const apiCurriculum =
    CURRICULUM_NAME_MAP[curriculum?.toLowerCase()] || curriculum;
  const apiSubject = SUBJECT_NAME_MAP[subject.toLowerCase()] || subject;
  return { apiCurriculum, apiSubject };
};

const courseNameToGrade = (courseName) => {
  if (!courseName) {
    return courseName;
  }
  if (courseName === "Kindergarten") {
    return "K";
  }
  const gradeMatch = courseName.match(/^(\d+)(?:st|nd|rd|th)\s+Grade/i);
  if (gradeMatch) {
    return gradeMatch[1];
  }
  return courseName;
};

// SAT grade short codes stored in the DB (PSAT8-9 / PSAT10 / SAT) don't
// match the curriculum repo's full course names. Map them explicitly.
const SAT_GRADE_TO_COURSE = {
  "PSAT8-9": "Digital PSAT 8/9",
  PSAT10: "Digital PSAT/NMSQT and PSAT 10",
  SAT: "Digital SAT",
};

// Match a DB grade label against an API course name. Mirrors the worker's
// gradeMatchesCourse — exact-match for full-form grades like
// "5th Grade: US History (1491-1850)" or "Digital PSAT 8/9", regex
// match for legacy short forms ("5"), and "K" → "Kindergarten".
const gradeMatchesCourse = (grade, courseName) => {
  if (!grade) {
    return false;
  }
  if (grade === "K") {
    return courseName === "Kindergarten" || courseName === grade;
  }
  if (/^\d+$/.test(grade)) {
    return courseName === grade || courseNameToGrade(courseName) === grade;
  }
  if (SAT_GRADE_TO_COURSE[grade] === courseName) {
    return true;
  }
  return (
    courseName === grade || courseName.toLowerCase() === grade.toLowerCase()
  );
};

/**
 * Build the query string for /view-tree calls, always opting in to
 * supported_formats so leaves that declare it (e.g. Athena-CC/Math) carry
 * the field through. No-op for curricula that don't declare it.
 */
const buildViewTreeQuery = (version) => {
  const parts = ["include_formats=true"];
  if (version) {
    parts.push(`version=${encodeURIComponent(version)}`);
  }
  return `?${parts.join("&")}`;
};

// Forward a curriculum API call through the proxy-curriculum edge function so
// the API key stays server-side. `path` is the upstream curriculum API path
// (e.g. "/view-tree?include_formats=true") and `body` is the upstream JSON
// body (already an object, not stringified). `method` defaults to POST;
// pass "GET" for endpoints like /list-curricula.
const apiRequest = async (path, body, method = "POST") => {
  const { data, error } = await supabase.functions.invoke("proxy-curriculum", {
    body: { path, body, method },
  });

  if (error) {
    // supabase-js wraps non-2xx responses as FunctionsHttpError with a
    // generic "non-2xx status code" message. The actual upstream body lives
    // on error.context (a Response). Surface it so callers see *why*.
    let detail = error.message || String(error);
    if (error.context && typeof error.context.text === "function") {
      try {
        const text = await error.context.text();
        if (text) {
          try {
            const parsed = JSON.parse(text);
            detail = parsed.error || parsed.message || text;
          } catch {
            detail = text;
          }
        }
      } catch {
        // fall through with the original message
      }
    }
    throw new Error(`Curriculum API ${path} failed: ${detail}`);
  }
  return data;
};

/**
 * Recursively walk a tree node's children array to collect leaf nodes.
 * The API returns nodes with { item_level, children: [...] }.
 * Leaves are: standards with is_leaf=true, or substandards.
 */
const collectLeaves = (node) => {
  const leaves = [];

  if (node.item_level === "substandard") {
    leaves.push({
      leafId: node.substandard_id,
      description: node.substandard_description || "",
      lessonName: null,
      supportedFormats: node.supported_formats || null,
    });
    return leaves;
  }

  if (node.item_level === "standard" && node.is_leaf) {
    leaves.push({
      leafId: node.standard_id,
      description: node.standard_description || "",
      lessonName: null,
      supportedFormats: node.supported_formats || null,
    });
    return leaves;
  }

  // Recurse into children
  if (node.children) {
    for (const child of node.children) {
      leaves.push(...collectLeaves(child));
    }
  }

  return leaves;
};

/**
 * Fetch leaves for a specific curriculum + subject + grade.
 *
 * SAT: grade (e.g. "PSAT10") maps directly to a course; whole course is target.
 * AP: grade is a unit number that maps to a domain within a fixed course.
 * Other: grade maps to a course (e.g. "3" → "3rd Grade").
 *
 * NOTE: `curriculum` arrives lowercased from the URL slug (toSlug in
 * BenchmarkSets). `resolveApiNames` re-canonicalizes via CURRICULUM_NAME_MAP
 * before calling upstream — when adding a new mixed-case curriculum
 * (e.g. "Khan-CC"), add its lowercase slug to that map or upstream will
 * 400 on the lowercased name.
 *
 * @param {string} curriculum - DB name
 * @param {string} subject - DB name
 * @param {string} grade - Grade label
 * @param {string} [version] - Curriculum version, null for latest
 * @returns {Promise<{data: Array, error: Object}>}
 */
const leavesForGradeCache = new Map();
const leavesForGradeCacheKey = (apiCurriculum, apiSubject, grade, version) =>
  [apiCurriculum || "", apiSubject || "", grade || "", version || ""].join("|");

const courseListCache = new Map();
const courseListCacheKey = (apiCurriculum, apiSubject, version) =>
  [apiCurriculum || "", apiSubject || "", version || ""].join("|");

const fetchCourseNames = (apiCurriculum, apiSubject, version, versionParam) => {
  const clKey = courseListCacheKey(apiCurriculum, apiSubject, version);
  if (!courseListCache.has(clKey)) {
    const promise = apiRequest(`/view-tree${versionParam}`, {
      ancestry: { curriculum: apiCurriculum, subject: apiSubject },
      prune_level: "course",
    }).then((data) => {
      const tree = data.tree;
      return tree && tree.children
        ? tree.children
            .filter((c) => c.item_level === "course")
            .map((c) => c.name)
        : [];
    });
    promise.catch(() => courseListCache.delete(clKey));
    courseListCache.set(clKey, promise);
  }
  return courseListCache.get(clKey);
};

const doFetchLeavesForGrade = async (curriculum, subject, grade, version) => {
  try {
    const { apiCurriculum, apiSubject } = resolveApiNames(curriculum, subject);
    const versionParam = buildViewTreeQuery(version);
    const subjectLower = subject.toLowerCase();

    if (AP_COURSE_MAP[subjectLower]) {
      const courseName = AP_COURSE_MAP[subjectLower];
      const domainPrefix = AP_DOMAIN_PREFIX[subjectLower];

      const courseData = await apiRequest(`/view-tree${versionParam}`, {
        ancestry: {
          curriculum: apiCurriculum,
          subject: apiSubject,
          course: courseName,
        },
      });

      const courseTree = courseData.tree;
      if (!courseTree || !courseTree.children) {
        return { data: [], error: null, courseName: null };
      }

      const targetPrefix = domainPrefix ? `${domainPrefix} ${grade}` : grade;
      const matchedDomain = courseTree.children.find(
        (child) =>
          child.item_level === "domain" &&
          (child.domain_id === targetPrefix ||
            (domainPrefix && child.domain_id.startsWith(targetPrefix)))
      );

      if (!matchedDomain) {
        return { data: [], error: null, courseName: null };
      }

      const leaves = collectLeaves(matchedDomain);
      return { data: leaves, error: null, courseName: null };
    }

    const courseNames = await fetchCourseNames(
      apiCurriculum,
      apiSubject,
      version,
      versionParam
    );

    const matchedCourse = courseNames.find((name) =>
      gradeMatchesCourse(grade, name)
    );

    if (!matchedCourse) {
      return { data: [], error: null, courseName: null };
    }

    const courseData = await apiRequest(`/view-tree${versionParam}`, {
      ancestry: {
        curriculum: apiCurriculum,
        subject: apiSubject,
        course: matchedCourse,
      },
    });

    const courseTree = courseData.tree;
    if (!courseTree) {
      return { data: [], error: null, courseName: matchedCourse };
    }

    const leaves = collectLeaves(courseTree);
    return { data: leaves, error: null, courseName: matchedCourse };
  } catch (error) {
    console.error("Error fetching leaves from curriculum API:", error);
    return { data: null, error, courseName: null };
  }
};

export const fetchLeavesForGrade = (curriculum, subject, grade, version) => {
  const cacheKey = leavesForGradeCacheKey(curriculum, subject, grade, version);
  if (!leavesForGradeCache.has(cacheKey)) {
    leavesForGradeCache.set(
      cacheKey,
      doFetchLeavesForGrade(curriculum, subject, grade, version)
    );
  }
  return leavesForGradeCache.get(cacheKey);
};

// =============================================================================
// API-driven discovery (used by TestEndpointModal so dropdowns show whatever
// the curriculum repo actually exposes, with no hardcoded name mappings).
// =============================================================================

/**
 * GET /list-curricula → list of { abbreviation, name, description }. The
 * abbreviation (e.g. "AP", "CC") is what every other endpoint expects as
 * the curriculum key, so we surface that as `id` for callers.
 */
export const fetchCurricula = async () => {
  try {
    const data = await apiRequest("/list-curricula", null, "GET");
    const items = (data?.curricula || []).map((c) =>
      typeof c === "string"
        ? { id: c, label: c, description: "" }
        : {
            id: c.abbreviation || c.name,
            label: c.name || c.abbreviation,
            description: c.description || "",
          }
    );
    return { data: items, error: null };
  } catch (error) {
    return { data: [], error };
  }
};

/**
 * POST /view-tree with prune_level "subject" → list of subject names under
 * the given curriculum, exactly as the curriculum repo exposes them.
 */
export const fetchSubjectsForCurriculum = async (curriculum) => {
  try {
    const data = await apiRequest("/view-tree?include_formats=false", {
      ancestry: { curriculum },
      prune_level: "subject",
    });
    const subjects = (data?.tree?.children || [])
      .filter((c) => c.item_level === "subject")
      .map((c) => c.name);
    return { data: subjects, error: null };
  } catch (error) {
    return { data: [], error };
  }
};

/**
 * POST /view-tree with prune_level "course" → list of course names under
 * the given curriculum + subject.
 */
export const fetchCoursesForSubject = async (curriculum, subject) => {
  try {
    const data = await apiRequest("/view-tree?include_formats=false", {
      ancestry: { curriculum, subject },
      prune_level: "course",
    });
    const courses = (data?.tree?.children || [])
      .filter((c) => c.item_level === "course")
      .map((c) => c.name);
    return { data: courses, error: null };
  } catch (error) {
    return { data: [], error };
  }
};

// In-memory cache for /fetch lookups (page lifetime). Capped to avoid
// unbounded growth across long sessions; oldest entries are dropped first.
const FETCH_CACHE_MAX = 200;
const fetchCache = new Map();
const fetchCacheKey = ({ itemId, curriculum, subject, course, version }) =>
  [itemId, curriculum || "", subject || "", course || "", version || ""].join(
    "|"
  );
const fetchCacheSet = (key, value) => {
  if (fetchCache.has(key)) {
    fetchCache.delete(key);
  } else if (fetchCache.size >= FETCH_CACHE_MAX) {
    const oldest = fetchCache.keys().next().value;
    if (oldest !== undefined) {
      fetchCache.delete(oldest);
    }
  }
  fetchCache.set(key, value);
};

/**
 * POST /fetch with full ancestry. Returns the first matched result (item +
 * attributes + ancestry), or null when nothing matches. Used to enrich the
 * RunDetail curriculum panel with the rich Incept Curriculum attributes
 * (key_concepts, lesson_name, full per-level difficulty_definitions, parent
 * standard, domain, cluster) that aren't stored on bm_runs.
 *
 * @param {object} args
 * @param {string} args.itemId       e.g. "CCSS.MATH.CONTENT.4.NBT.A.2+1"
 * @param {string} [args.curriculum] raw DB value (e.g. "Athena-CC")
 * @param {string} [args.subject]    raw DB value (e.g. "math")
 * @param {string} [args.course]
 * @param {string} [args.version]
 * @returns {Promise<{data: object|null, error: Error|null}>}
 */
export const fetchCurriculumItem = async ({
  itemId,
  curriculum,
  subject,
  course,
  version,
}) => {
  if (!itemId) {
    return { data: null, error: new Error("itemId required") };
  }
  const key = fetchCacheKey({ itemId, curriculum, subject, course, version });
  if (fetchCache.has(key)) {
    return { data: fetchCache.get(key), error: null };
  }

  try {
    const apiCurriculum = curriculum
      ? CURRICULUM_NAME_MAP[curriculum.toLowerCase()] || curriculum
      : null;
    const apiSubject = subject
      ? SUBJECT_NAME_MAP[subject.toLowerCase()] || subject
      : null;

    const ancestry = {};
    if (apiCurriculum) {
      ancestry.curriculum = apiCurriculum;
    }
    if (apiSubject) {
      ancestry.subject = apiSubject;
    }
    if (course) {
      ancestry.course = course;
    }

    const path = version
      ? `/fetch?version=${encodeURIComponent(version)}`
      : "/fetch";

    const data = await apiRequest(path, {
      item_id: itemId,
      target_level: "leaf",
      ancestry,
    });
    // Same substandard id can appear under multiple domains/clusters with
    // different attribute fill rates. Prefer a result whose attributes are
    // actually populated, falling back to the first match.
    const results = Array.isArray(data?.results) ? data.results : [];
    const hasAttrs = (r) => {
      const a = r?.item?.attributes;
      if (!a) {
        return false;
      }
      return Boolean(
        a.lesson_name ||
        (Array.isArray(a.key_concepts) && a.key_concepts.length) ||
        (Array.isArray(a.learning_objectives) &&
          a.learning_objectives.length) ||
        (Array.isArray(a.assessment_boundaries) &&
          a.assessment_boundaries.length) ||
        (Array.isArray(a.common_misconceptions) &&
          a.common_misconceptions.length) ||
        (a.difficulty_definitions &&
          Object.values(a.difficulty_definitions).some(Boolean))
      );
    };
    const populated = results.find(hasAttrs);
    const result = populated || results[0] || null;
    fetchCacheSet(key, result);
    return { data: result, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * POST /list-leaves under a fully-specified curriculum/subject/course
 * ancestry. Each leaf has { leafId, description, supportedFormats }.
 */
export const fetchLeavesForCourse = async (curriculum, subject, course) => {
  try {
    const data = await apiRequest("/list-leaves?include_formats=true", {
      ancestry: { curriculum, subject, course },
    });
    const leaves = (data?.results || []).map((leaf) => ({
      leafId: leaf.substandard_id || leaf.standard_id,
      description:
        leaf.substandard_description || leaf.standard_description || "",
      supportedFormats: leaf.supported_formats || null,
    }));
    return { data: leaves, error: null };
  } catch (error) {
    return { data: [], error };
  }
};
